import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { WeekStreak } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { progressWidgetLabel } from "./progressWidgetCatalog";
import { makeProgressViewStyles } from "./progressViewStyles";
import { WidgetTitleRow } from "./InfoPill";
import { ProgressWidgetStatus } from "./ProgressWidgetStatus";
import { WeekBarsCard } from "./WeekBarsCard";

// PG-58: the consistency streak, the current run of weeks with a session and
// the best one. The sentence says it all in words. PG-64 fetta 2: under it,
// the sessions of each of the last weeks, written on its column (the Rhythm's
// own counts): with an unbroken run a tall-or-low bar said nothing. The
// current week is the in-progress outline with its count, since it already
// counts. ⛔ No nudge to «keep the streak alive» (the Don't-list).
export function ProgressWeekStreakCard({
  theme,
  streak,
  summaryError,
  weeklyCounts,
  nowMs,
  weekStartDay,
  locale,
}: {
  theme: UiTheme;
  /** null while the sessions load. */
  streak: WeekStreak | null;
  summaryError: string | null;
  /** Sessions per calendar week, oldest first, the current week last. */
  weeklyCounts: number[];
  nowMs: number;
  weekStartDay?: number;
  locale: string;
}) {
  const { t } = useTranslation();
  const styles = makeProgressViewStyles(theme);
  const titleRow = (
    <WidgetTitleRow theme={theme} title={progressWidgetLabel(t, "weekStreak")} />
  );
  if (summaryError !== null || streak === null) {
    return (
      <ProgressWidgetStatus
        theme={theme}
        titleRow={titleRow}
        error={summaryError}
        loading={streak === null}
      />
    );
  }
  if (streak.best === 0) {
    return (
      <View style={styles.rhythm}>
        {titleRow}
        <Text style={styles.cardHelp}>
          {t("mobile.progress.weekStreakEmpty", {
            defaultValue: "No sessions yet.",
          })}
        </Text>
      </View>
    );
  }

  const stats: Array<[string, number]> = [
    [
      t("mobile.progress.weekStreakCurrent", { defaultValue: "Current streak" }),
      streak.current,
    ],
    [
      t("mobile.progress.weekStreakBest", { defaultValue: "Best streak" }),
      streak.best,
    ],
  ];
  const sentence =
    streak.current === 0
      ? t("mobile.progress.weekStreakNone", {
          defaultValue:
            "No session last week and none yet this week. This week is still open.",
        })
      : streak.currentWeekHasSession
        ? t("mobile.progress.weekStreakWithThisWeek", {
            count: streak.current,
            defaultValue:
              "{{count}} weeks in a row with at least one session, this week included.",
          })
        : t("mobile.progress.weekStreakUpToLastWeek", {
            count: streak.current,
            defaultValue:
              "{{count}} weeks in a row with at least one session, up to last week. This week is still open.",
          });

  return (
    <View style={styles.rhythm}>
      {titleRow}
      <View style={styles.statGrid}>
        {stats.map(([label, n]) => {
          const unit = t("mobile.progress.weekStreakWeeks", {
            count: n,
            defaultValue: "weeks",
          });
          return (
            <View
              key={label}
              style={styles.stat}
              accessible
              accessibilityLabel={t("mobile.progress.weekStreakStatA11y", {
                label,
                count: n,
                defaultValue: "{{label}}: {{count}} weeks",
              })}
            >
              <Text style={styles.statLabel}>{label}</Text>
              <View style={styles.statValueRow}>
                <Text style={styles.statValue}>{n}</Text>
                <Text style={styles.cardHelp}>{unit}</Text>
              </View>
            </View>
          );
        })}
      </View>
      <View style={styles.card}>
        <Text style={styles.cardHelp}>{sentence}</Text>
        <WeekBarsCard
          theme={theme}
          title={t("mobile.progress.weekBars.sessions", { defaultValue: "Sessions" })}
          values={weeklyCounts}
          nowMs={nowMs}
          weekStartDay={weekStartDay}
          locale={locale}
          valueFormat={String}
          partialLast
        />
        <Text style={styles.cardHelp}>
          {/* No week count: how many bars show depends on the width. */}
          {t("mobile.progress.weekStreakSessionsHelp", {
            defaultValue:
              "Sessions in each week. The outlined column is this week: it already counts.",
          })}
        </Text>
      </View>
    </View>
  );
}
